import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../api";

export const PRICE_VISIBILITY_STORAGE_KEY = "tcs_show_prices_sitewide";
const preferenceRequests = new Map();
export const fetchPreferencesOnce = (userId, { refresh = false } = {}) => {
  const key = String(userId || "");
  if (refresh) preferenceRequests.delete(key);
  if (!preferenceRequests.has(key)) preferenceRequests.set(key, api.get("/api/users/me/preferences"));
  return preferenceRequests.get(key);
};
export const cachePreferenceResponse = (userId, data) => preferenceRequests.set(String(userId || ""), Promise.resolve({ data }));

const PriceVisibilityContext = createContext({ showPrices: true, pricesLoaded: true, setShowPrices: () => {}, refreshPricePreference: async () => {} });
const readCached = () => {
  const value = localStorage.getItem(PRICE_VISIBILITY_STORAGE_KEY);
  return value === "false" ? false : value === "true" ? true : null;
};

export function PriceVisibilityProvider({ user, authReady, children }) {
  const userId = user?.id ?? user?.user_id ?? null;
  const initialCached = readCached();
  // Auth hydration happens after the first render. Honor the cached value
  // during that window so hidden prices cannot flash before the user loads.
  const [showPrices, setShowPricesState] = useState(initialCached != null ? initialCached : true);
  const [pricesLoaded, setPricesLoaded] = useState(initialCached != null);

  const setShowPrices = useCallback((value) => {
    const next = Boolean(value);
    setShowPricesState(next);
    setPricesLoaded(true);
    if (userId) localStorage.setItem(PRICE_VISIBILITY_STORAGE_KEY, String(next));
  }, [userId]);

  const refreshPricePreference = useCallback(async ({ refresh = false } = {}) => {
    if (!userId) { setShowPricesState(true); setPricesLoaded(true); return true; }
    try {
      const { data } = await fetchPreferencesOnce(userId, { refresh });
      const next = data?.show_prices_sitewide !== false;
      setShowPricesState(next);
      localStorage.setItem(PRICE_VISIBILITY_STORAGE_KEY, String(next));
      setPricesLoaded(true);
      return next;
    } catch {
      const cached = readCached();
      setShowPricesState(cached == null ? true : cached);
      setPricesLoaded(true);
      return cached == null ? true : cached;
    }
  }, [userId]);

  useEffect(() => {
    if (!authReady) return;
    if (!userId) {
      localStorage.removeItem(PRICE_VISIBILITY_STORAGE_KEY);
      setShowPricesState(true);
      setPricesLoaded(true);
      return;
    }
    const cached = readCached();
    setShowPricesState(cached == null ? true : cached);
    setPricesLoaded(cached != null);
    refreshPricePreference();
  }, [authReady, userId, refreshPricePreference]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== PRICE_VISIBILITY_STORAGE_KEY) return;
      setShowPricesState(event.newValue !== "false");
      setPricesLoaded(true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ showPrices, pricesLoaded, setShowPrices, refreshPricePreference }), [showPrices, pricesLoaded, setShowPrices, refreshPricePreference]);
  return <PriceVisibilityContext.Provider value={value}>{children}</PriceVisibilityContext.Provider>;
}

export const usePriceVisibility = () => useContext(PriceVisibilityContext);
export function PriceValue({ children }) {
  const { showPrices, pricesLoaded } = usePriceVisibility();
  if (!pricesLoaded || !showPrices) return null;
  return children;
}
export function PriceVisibilityBoundary({ children }) {
  const { showPrices, pricesLoaded } = usePriceVisibility();
  if (!pricesLoaded || !showPrices) return null;
  return children;
}
